/* =========================================================
   Reports — the filter rail's controls, the column picker and
   the cell renderers the plain column catalogue can't carry.
   ========================================================= */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Lang } from '@/types';
import { ic, sized, type IconElement } from '@/components/Icons';
import { ds } from '@/data';
import {
  CATS, CAT_META, COLS, L, buildConsolidated, defaultCols, fullName,
  type Category, type ColSelection, type Comp, type Filters, type ReportRow,
} from './model';

export const FLD_LABEL: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 800, color: 'var(--text-2)',
  textTransform: 'uppercase', letterSpacing: '.05em',
};

export const INPUT: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)',
  fontSize: 13, background: 'var(--surface)', width: '100%', color: 'var(--text)',
  fontFamily: 'inherit',
};

/* ---------------- completeness cell ---------------- */

export function CompCell({ comp }: { comp: Comp }) {
  const color = comp.pct === 100 ? 'var(--ok)' : comp.pct >= 60 ? 'var(--brand-primary)' : 'var(--warn)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 96 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--bg-2)', borderRadius: 99, overflow: 'hidden', minWidth: 44 }}>
        <div style={{ width: comp.pct + '%', height: '100%', background: color, borderRadius: 99 }} />
      </div>
      <span className="mono" style={{ fontSize: 11, fontWeight: 700, color, width: 30, textAlign: 'right' }}>
        {comp.pct}%
      </span>
    </div>
  );
}

const dash = <span style={{ color: 'var(--text-3)' }}>—</span>;
const s = (v: unknown) => (v == null ? '' : String(v));

/** Renderers that need JSX, keyed by category then column. Everything else
 *  falls back to the column's plain `get`. */
export const CELLS: Partial<Record<Category, Record<string, (r: ReportRow, lang: Lang) => ReactNode>>> = {
  orgs: {
    _comp: (r) => <CompCell comp={r._comp as Comp} />,
  },
  sites: {
    _comp: (r) => <CompCell comp={r._comp as Comp} />,
    _manager: (r, l) =>
      r._manager
        ? fullName(s(r.SiteManagerFirstName), s(r.SiteManagerLastName))
        : <span className="chip warn">{L(l, 'Sin asignar', 'Unassigned')}</span>,
    _primary: (r, l) =>
      r._primary
        ? <span className="chip brand">{sized(ic.star, 11)} {L(l, 'Primario', 'Primary')}</span>
        : dash,
  },
  instructors: {
    Certification: (r) => (r.Certification ? <span className="chip">{s(r.Certification)}</span> : dash),
    _status: (r, l) =>
      r._status === 'disassociated'
        ? <span className="chip warn">{L(l, 'Desasociado', 'Disassociated')}</span>
        : <span className="chip ok">{L(l, 'Activo', 'Active')}</span>,
  },
  consolidated: {
    _iname: (r, l) =>
      r.instr_InstructorId
        ? fullName(s(r.instr_EnglishFirstName), s(r.instr_EnglishLastName))
        : r.site_SiteId
          ? <span className="chip warn">{L(l, 'Sin instructores', 'No instructors')}</span>
          : dash,
  },
};

/* ---------------- small controls ---------------- */

export function Ctl({ label, children, icon }: { label: ReactNode; children: ReactNode; icon?: IconElement }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <span style={{ ...FLD_LABEL, display: 'flex', alignItems: 'center', gap: 5 }}>
        {icon ? <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}>{sized(icon, 12)}</span> : null}
        {label}
      </span>
      {children}
    </label>
  );
}

export interface SegOption {
  v: string;
  label: string;
}

export function Seg({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: SegOption[] }) {
  return (
    <div role="group" style={{ display: 'flex', background: 'var(--bg-2)', borderRadius: 8, padding: 2, border: '1px solid var(--line)' }}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          aria-pressed={value === o.v}
          className="btn sm"
          style={{
            flex: 1,
            background: value === o.v ? 'var(--surface)' : 'transparent',
            border: 'none',
            boxShadow: value === o.v ? 'var(--shadow-1)' : 'none',
            color: value === o.v ? 'var(--brand-ink)' : 'var(--text-2)',
            fontWeight: 700, fontSize: 11, padding: '5px 6px', whiteSpace: 'nowrap',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- category picker ---------------- */

export function CategoryPicker({
  value, onChange, lang, big,
}: { value: Category; onChange: (c: Category) => void; lang: Lang; big?: boolean }) {
  const counts: Record<Category, number> = {
    orgs: ds.listOrgs().length,
    sites: ds.listSites().length,
    instructors: ds.listInstructors().length,
    consolidated: buildConsolidated().length,
  };
  return (
    <div
      role="tablist"
      aria-label={L(lang, 'categoría', 'category')}
      style={{ display: big ? 'flex' : 'grid', gridTemplateColumns: big ? undefined : '1fr', gap: big ? 8 : 6 }}
    >
      {CATS.map((c) => {
        const m = CAT_META[c];
        const active = value === c;
        return (
          <button
            key={c}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(c)}
            style={{
              flex: big ? 1 : undefined, display: 'flex', alignItems: 'center', gap: 11,
              padding: big ? '14px 16px' : '11px 12px', cursor: 'pointer', textAlign: 'left',
              border: '1px solid ' + (active ? 'var(--brand-primary)' : 'var(--line)'),
              background: active ? 'var(--brand-primary-l)' : 'var(--surface)',
              borderRadius: 10, transition: 'all .15s ease', fontFamily: 'inherit',
              boxShadow: active ? '0 0 0 1px var(--brand-primary)' : 'none',
            }}
          >
            <span
              style={{
                width: big ? 40 : 34, height: big ? 40 : 34, borderRadius: 9, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: active ? 'var(--brand-primary)' : 'var(--bg-2)',
                color: active ? '#fff' : 'var(--text-2)',
              }}
            >
              {sized(m.icon, big ? 21 : 18)}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 800, fontSize: big ? 15 : 13.5, color: active ? 'var(--brand-primary-d)' : 'var(--text)', letterSpacing: '-.01em' }}>
                {L(lang, m.es, m.en)}
              </span>
              <span className="mono" style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>
                {counts[c]} {L(lang, 'registros', 'records')}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- filter blocks ---------------- */

/** Only what the country <select> needs — `Country` also carries `cities`. */
export interface CountryOpt {
  code: string;
  name_es: string;
  name_en: string;
}

export interface Bounds {
  min: number;
  max: number;
}

export type Patch = (p: Partial<Filters>) => void;

export function TimeFilters({
  f, upd, lang, bounds, twoCol,
}: { f: Filters; upd: Patch; lang: Lang; bounds: Bounds; twoCol?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: twoCol ? '1fr 1fr' : '1fr', gap: 12 }}>
      <Ctl label={L(lang, 'Año de ingreso', 'Year joined')} icon={ic.flag}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="number" value={f.yearMin} min={bounds.min} max={bounds.max} placeholder={String(bounds.min)}
            onChange={(e) => upd({ yearMin: e.target.value })}
            style={{ ...INPUT, textAlign: 'center' }} aria-label={L(lang, 'Año desde', 'Year from')}
          />
          <span style={{ color: 'var(--text-3)', fontWeight: 700 }}>–</span>
          <input
            type="number" value={f.yearMax} min={bounds.min} max={bounds.max} placeholder={String(bounds.max)}
            onChange={(e) => upd({ yearMax: e.target.value })}
            style={{ ...INPUT, textAlign: 'center' }} aria-label={L(lang, 'Año hasta', 'Year to')}
          />
        </div>
      </Ctl>
      <Ctl label={L(lang, 'Última actualización', 'Last updated')} icon={ic.refresh}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" value={f.updatedFrom} onChange={(e) => upd({ updatedFrom: e.target.value })} style={INPUT} aria-label={L(lang, 'Desde', 'From')} />
          <span style={{ color: 'var(--text-3)', fontWeight: 700 }}>–</span>
          <input type="date" value={f.updatedTo} onChange={(e) => upd({ updatedTo: e.target.value })} style={INPUT} aria-label={L(lang, 'Hasta', 'To')} />
        </div>
      </Ctl>
    </div>
  );
}

export function SpecificFilters({
  category, f, upd, lang, countryOpts, certOpts, cols,
}: {
  category: Category; f: Filters; upd: Patch; lang: Lang;
  countryOpts: CountryOpt[]; certOpts: string[]; cols: number;
}) {
  const compOpts: SegOption[] = [
    { v: 'all', label: L(lang, 'Todos', 'All') },
    { v: 'complete', label: L(lang, 'Completos', 'Complete') },
    { v: 'pending', label: L(lang, 'Pendientes', 'Pending') },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 12 }}>
      <Ctl label={L(lang, 'País / región', 'Country / region')} icon={ic.globe}>
        <select value={f.country} onChange={(e) => upd({ country: e.target.value })} style={INPUT}>
          <option value="">{L(lang, 'Todos los países', 'All countries')}</option>
          {countryOpts.map((c) => (
            <option key={c.code} value={c.code}>{lang === 'es' ? c.name_es : c.name_en}</option>
          ))}
        </select>
      </Ctl>

      {category !== 'consolidated' ? (
        <Ctl label={L(lang, 'Completitud del registro', 'Record completeness')} icon={ic.check}>
          <Seg value={f.completeness} onChange={(v) => upd({ completeness: v as Filters['completeness'] })} options={compOpts} />
        </Ctl>
      ) : null}

      {category === 'consolidated' ? (
        <Ctl label={L(lang, 'Cobertura de instructores', 'Instructor coverage')} icon={ic.users}>
          <Seg
            value={f.status}
            onChange={(v) => upd({ status: v })}
            options={[
              { v: 'all', label: L(lang, 'Todas', 'All') },
              { v: 'with', label: L(lang, 'Con instr.', 'With instr.') },
              { v: 'without', label: L(lang, 'Sin instr.', 'Without') },
            ]}
          />
        </Ctl>
      ) : null}

      {category === 'orgs' ? (
        <Ctl label={L(lang, 'Cobertura de sites', 'Site coverage')} icon={ic.site}>
          <Seg
            value={f.multiSite}
            onChange={(v) => upd({ multiSite: v as Filters['multiSite'] })}
            options={[
              { v: 'all', label: L(lang, 'Todas', 'All') },
              { v: 'multi', label: 'Multi-site' },
              { v: 'single', label: L(lang, 'Un site', 'Single') },
            ]}
          />
        </Ctl>
      ) : null}

      {category === 'sites' ? (
        <>
          <Ctl label={L(lang, 'Tiene Site Manager', 'Has Site Manager')} icon={ic.user}>
            <Seg
              value={f.hasManager}
              onChange={(v) => upd({ hasManager: v as Filters['hasManager'] })}
              options={[
                { v: 'all', label: L(lang, 'Todos', 'All') },
                { v: 'yes', label: L(lang, 'Sí', 'Yes') },
                { v: 'no', label: 'No' },
              ]}
            />
          </Ctl>
          <Ctl label={L(lang, 'Site primario', 'Primary site')} icon={ic.star}>
            <Seg
              value={f.primary}
              onChange={(v) => upd({ primary: v as Filters['primary'] })}
              options={[
                { v: 'all', label: L(lang, 'Todos', 'All') },
                { v: 'yes', label: L(lang, 'Sí', 'Yes') },
                { v: 'no', label: 'No' },
              ]}
            />
          </Ctl>
        </>
      ) : null}

      {category === 'instructors' ? (
        <>
          <Ctl label={L(lang, 'Estado', 'Status')} icon={ic.instr}>
            <Seg
              value={f.status}
              onChange={(v) => upd({ status: v })}
              options={[
                { v: 'all', label: L(lang, 'Todos', 'All') },
                { v: 'active', label: L(lang, 'Activos', 'Active') },
                { v: 'disassociated', label: L(lang, 'Desasociados', 'Disassociated') },
              ]}
            />
          </Ctl>
          <Ctl label={L(lang, 'Certificación', 'Certification')} icon={ic.shield}>
            <select value={f.certification} onChange={(e) => upd({ certification: e.target.value })} style={INPUT}>
              <option value="">{L(lang, 'Todas', 'All')}</option>
              {certOpts.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Ctl>
        </>
      ) : null}
    </div>
  );
}

/* ---------------- column picker ---------------- */

export function ColumnPicker({
  category, cols, setCols, lang,
}: {
  category: Category;
  cols: ColSelection;
  setCols: React.Dispatch<React.SetStateAction<ColSelection>>;
  lang: Lang;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const all = COLS[category];
  const sel = cols[category];

  function toggle(key: string) {
    setCols((st) => {
      const cur = st[category];
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      if (next.length === 0) return st; // keep at least one column
      return { ...st, [category]: next };
    });
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {ic.columns} {L(lang, 'Columnas', 'Columns')}
        <span className="chip brand" style={{ padding: '1px 7px', fontSize: 10 }}>{sel.length}/{all.length}</span>
      </button>
      {open ? (
        <div
          className="card"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60, width: 280,
            boxShadow: 'var(--shadow-3)', border: 'none', padding: 10, maxHeight: 420, overflow: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 4px 8px' }}>
            <span style={FLD_LABEL}>{L(lang, 'Columnas del reporte', 'Report columns')}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                className="btn sm ghost" style={{ fontSize: 11, padding: '3px 6px' }}
                onClick={() => setCols((st) => ({ ...st, [category]: all.map((c) => c.key) }))}
              >
                {L(lang, 'Todas', 'All')}
              </button>
              <button
                className="btn sm ghost" style={{ fontSize: 11, padding: '3px 6px' }}
                onClick={() => setCols((st) => ({ ...st, [category]: defaultCols()[category] }))}
              >
                {L(lang, 'Por defecto', 'Default')}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {all.map((c) => {
              const on = sel.includes(c.key);
              return (
                <button
                  key={c.key}
                  onClick={() => toggle(c.key)}
                  className="btn ghost"
                  role="checkbox"
                  aria-checked={on}
                  style={{ justifyContent: 'flex-start', gap: 9, padding: '7px 8px', fontWeight: on ? 700 : 500 }}
                >
                  <span
                    style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      border: '1.5px solid ' + (on ? 'var(--brand-primary)' : 'var(--line-2)'),
                      background: on ? 'var(--brand-primary)' : 'var(--surface)',
                      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {on ? sized(ic.check, 12, 3) : null}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>{L(lang, c.es, c.en)}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- export bar + active chips ---------------- */

export interface Exporters {
  onXlsx: () => void;
  onCsv: () => void;
  onPdf: () => void;
  disabled: boolean;
}

export function ExportBar({ onXlsx, onCsv, onPdf, disabled }: Exporters) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button className="btn primary" onClick={onXlsx} disabled={disabled}>{ic.excel} Excel</button>
      <button className="btn" onClick={onCsv} disabled={disabled}>{ic.download} CSV</button>
      <button className="btn" onClick={onPdf} disabled={disabled}>{ic.table} PDF</button>
    </div>
  );
}

export interface Chip {
  label: string;
  clear: () => void;
}

export function ActiveFilters({ chips, lang, onClearAll }: { chips: Chip[]; lang: Lang; onClearAll: () => void }) {
  if (!chips.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>{L(lang, 'Filtros activos:', 'Active filters:')}</span>
      {chips.map((c, i) => (
        <button
          key={i}
          onClick={c.clear}
          className="chip brand"
          style={{ cursor: 'pointer', border: 'none' }}
          title={L(lang, 'Quitar filtro', 'Remove filter')}
        >
          {c.label} {sized(ic.close, 11)}
        </button>
      ))}
      <button onClick={onClearAll} className="btn sm ghost" style={{ fontSize: 11, color: 'var(--brand-primary-d)' }}>
        {L(lang, 'Limpiar todo', 'Clear all')}
      </button>
    </div>
  );
}
